import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import MainHamburgerMenu from './MainHamburgerMenu';
import { useTranslation } from '../hooks/useTranslation';

const OrderNavigation = ({ 
  currentTab, 
  setCurrentTab, 
  availableTabs, 
  userRole, 
  isActiveScreen = 'orders' // 'orders' or 'new-order'
}) => {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === 'ios';
  const { t } = useTranslation('navigation');

  // Mobile width detection for web
  const [isMobileWeb, setIsMobileWeb] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobileWeb(!isNative && window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isNative]);

  // Platform-specific styles
  const getNavStyles = () => {
    if (!isNative) {
      if (isMobileWeb) {
        // Mobile web styles (compact, icon-only)
        return {
          padding: '0.25rem 0.5rem',
          borderRadius: 0,
          marginBottom: '1rem',
          minHeight: '48px',
          height: '48px',
          boxShadow: 'var(--shadow-md)',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 0,
          background: 'var(--card-background)'
        };
      }
      // Web styles - original design
      return {
        background: 'var(--card-background)',
        padding: '0.75rem',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      };
    }
    // Native styles - fixed bottom navigation
    // Rendered via portal to document.body to escape WebView scrollView context
    return {
      position: 'fixed',
      bottom: 0, // Always at bottom, no adjustments
      left: 0,
      right: 0,
      background: isIOS ? 'rgba(255, 255, 255, 0.9)' : 'var(--card-background)',
      backdropFilter: isIOS ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isIOS ? 'blur(10px)' : 'none',
      padding: '0.75rem',
      paddingBottom: 'var(--safe-bottom)', // Safe area for iOS home bar
      boxShadow: isIOS ? '0 -2px 10px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
      zIndex: 1000,
      borderRadius: 0,
      margin: 0,
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      height: '76px',
      flexWrap: 'nowrap',
      borderTop: '1px solid var(--border-color)',
      borderBottom: isIOS ? '1px solid #E5E5EA' : 'none',
      // Force hardware acceleration
      transform: 'translateZ(0)',
      willChange: 'transform'
    };
  };

  const getButtonStyles = (isActive = false, isProfile = false) => {
    if (!isNative) {
      if (isMobileWeb) {
        // Mobile web styles (compact, icon-only) - Use primary color for active tabs
        return {
          padding: '0.25rem 0.25rem',
          minWidth: '36px',
          width: '36px',
          height: '36px',
          fontSize: '1.2rem',
          borderRadius: 0, // Remove circular shape
          background: isActive ? 'var(--primary-color)' : 'transparent', // Use primary color for active tabs
          color: isActive ? 'white' : 'inherit', // White text for active tabs
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          margin: 0,
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        };
      }
      // Web styles - Use primary color for active tabs, secondary for inactive
      return {
        backgroundColor: isActive ? 'var(--primary-color)' : (isProfile ? 'var(--primary-color)' : 'var(--secondary-color)'),
        color: 'white',
        padding: '0.5rem 0.75rem',
        fontSize: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        whiteSpace: 'nowrap',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginLeft: isProfile ? 'auto' : '0'
      };
    }
    // Native styles - mobile bottom navigation
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      background: 'transparent',
      color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
      border: 'none',
      borderRadius: 0,
      transition: 'all 0.2s',
      flex: 0,
      minWidth: '40px',
      width: '40px',
      height: '40px',
      cursor: 'pointer'
    };
  };

  const getIconStyles = () => {
    if (!isNative) {
      if (isMobileWeb) {
        return { fontSize: '1.3rem' };
      }
      return { fontSize: '1rem' };
    }
    return { fontSize: '1.5rem' };
  };

  const getTextStyles = () => {
    if (!isNative) {
      if (isMobileWeb) {
        return { display: 'none' };
      }
      return { fontWeight: '500' };
    }
    return { display: 'none' }; // Hide text on mobile
  };

  const getButtonsContainerStyles = () => {
    if (!isNative) {
      if (isMobileWeb) {
        return {
          display: 'flex',
          gap: 0,
          justifyContent: 'space-around',
          flex: 1
        };
      }
      return {
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'nowrap'
      };
    }
    return {
      display: 'flex',
      gap: 0,
      justifyContent: 'space-around',
      flex: 1
    };
  };

  const handleTabClick = (tab) => {
    if (isActiveScreen === 'orders') {
      setCurrentTab(tab);
    } else {
      navigate('/orders');
    }
  };

  const handleNewOrderClick = () => {
    navigate('/new-order');
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  const navContent = (
    <nav style={getNavStyles()}>
      <div style={getButtonsContainerStyles()}>
        {availableTabs.map(tab => (
          <button 
            key={tab}
            onClick={() => handleTabClick(tab)} 
            style={getButtonStyles(
              isActiveScreen === 'orders' && currentTab === tab
            )}
          >
            <span style={getIconStyles()}>
              {tab === 'PLACED' && '📋'}
              {tab === 'APPROVED' && '✅'}
              {tab === 'READY_TO_DISPATCH' && '📦'}
              {tab === 'DISPATCHED' && '🚚'}
            </span>
            <span style={getTextStyles()}>
              {tab === 'PLACED' && t('orders.placed')}
              {tab === 'APPROVED' && t('orders.approved')}
              {tab === 'READY_TO_DISPATCH' && t('orders.readyToDispatch')}
              {tab === 'DISPATCHED' && t('orders.dispatched')}
            </span>
          </button>
        ))}
        
        {userRole !== 'InventoryManager' && (
          <button 
            onClick={handleNewOrderClick}
            style={getButtonStyles(isActiveScreen === 'new-order')}
          >
            <span style={getIconStyles()}>➕</span>
            <span style={getTextStyles()}>{t('orders.newOrder')}</span>
          </button>
        )}
        
        {userRole === 'BusinessOwner' && (
          <button 
            onClick={handleDashboardClick}
            style={getButtonStyles()}
          >
            <span style={getIconStyles()}>📊</span>
            <span style={getTextStyles()}>{t('dashboard.title')}</span>
          </button>
        )}
      </div>
      
      <MainHamburgerMenu />
    </nav>
  );

  // For native iOS, render via portal to document.body to escape WebView scrollView context
  // This ensures position: fixed works correctly even when keyboard appears
  if (isNative && isIOS) {
    return createPortal(navContent, document.body);
  }

  // For web/PWA, render normally
  return navContent;
};

export default OrderNavigation; 